import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import { APP_CURRENCY, APP_LOCALE } from "../../config/locale";
import { getArticleById } from "../../catalogo/catalogoApi";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../components/SearchableSelect";
import {
  LOOKUP_SEARCH_PAGE_SIZE,
  searchLookupOptions,
} from "../../lookups/lookupsApi";
import {
  getWorkTypeById,
  saveWorkTypeTemplate,
  type WorkTypeTemplateLine,
  type WorkTypeTemplateLineInput,
} from "../../tiposObra/tiposObraApi";

const formatCurrency = (value: number): string => {
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(APP_LOCALE, {
    style: "currency",
    currency: APP_CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value).replace(/\./g, " ");
};

const parseNumber = (value: string): number => {
  const normalized = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!normalized) return 0;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Each editable row carries a stable client-side `_key` so React reconciles rows
// by identity (not array index). Using the index as the key desynchronises
// controlled inputs/checkboxes when rows are added or removed.
interface EditableLine extends WorkTypeTemplateLineInput {
  _key: number;
}

const DEFAULT_GROUP = "Geral";

const blankLineFields = (
  areaName: string,
  chapterName: string,
): WorkTypeTemplateLineInput => ({
  areaName,
  chapterName,
  articleId: null,
  code: "",
  description: "",
  unit: "",
  quantity: 0,
  quantityPerArea: false,
  unitPrice: 0,
  marginPercent: null,
});

const inputClass =
  "px-2 py-2 border border-border rounded bg-background text-foreground w-full";

const searchArticleOptions = async (
  term: string,
  signal: AbortSignal,
): Promise<SearchableSelectOption<number>[]> => {
  const results = await searchLookupOptions("articles", term, signal);
  return results.map((option) => ({
    value: option.id,
    label: option.label,
    description: option.description ?? undefined,
    keywords: option.keywords ?? undefined,
  }));
};

const articleSeedLabel = (line: EditableLine): string => {
  const parts = [line.code, line.description]
    .map((part) => part.trim())
    .filter((part) => part !== "");
  return parts.join(" · ") || `Artigo ${line.articleId}`;
};

export function WorkTypeTemplateView() {
  const navigate = useNavigate();
  const params = useParams();
  const workTypeId = Number(params.workTypeId);

  const [name, setName] = useState("");
  const [lines, setLines] = useState<EditableLine[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [priceWarning, setPriceWarning] = useState<string | null>(null);

  const keyCounter = useRef(0);
  const nextKey = useCallback(() => {
    keyCounter.current += 1;
    return keyCounter.current;
  }, []);

  const toEditable = useCallback(
    (line: WorkTypeTemplateLine): EditableLine => ({
      _key: nextKey(),
      areaName: line.areaName,
      chapterName: line.chapterName,
      articleId: line.articleId ?? null,
      code: line.code,
      description: line.description,
      unit: line.unit,
      quantity: line.quantity,
      quantityPerArea: line.quantityPerArea,
      unitPrice: line.unitPrice,
      marginPercent: line.marginPercent ?? null,
    }),
    [nextKey],
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const detail = await getWorkTypeById(workTypeId);
      setName(detail.name);
      setLines(detail.templateLines.map(toEditable));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar o tipo de obra.");
    } finally {
      setIsLoading(false);
    }
  }, [workTypeId, toEditable]);

  useEffect(() => {
    if (!Number.isFinite(workTypeId)) {
      setError("Tipo de obra inválido.");
      setIsLoading(false);
      return;
    }
    load();
  }, [load, workTypeId]);

  const updateLine = (key: number, patch: Partial<EditableLine>) => {
    setLines((prev) => prev.map((line) => (line._key === key ? { ...line, ...patch } : line)));
    setSavedMessage(null);
  };

  const handleArticleSelect = (
    key: number,
    articleId: number | null,
    option: SearchableSelectOption<number> | null,
  ) => {
    if (articleId === null) {
      updateLine(key, { articleId: null });
      return;
    }
    // A opção traz código (keywords), nome (label) e unidade (description).
    setLines((prev) =>
      prev.map((line) =>
        line._key === key
          ? {
              ...line,
              articleId,
              code: option?.keywords ?? line.code,
              description:
                line.description.trim() === "" && option
                  ? option.label
                  : line.description,
              unit: option?.description ?? line.unit,
            }
          : line,
      ),
    );
    setSavedMessage(null);
    setPriceWarning(null);

    // A projeção /api/articles/options não traz o preço de referência (de propósito);
    // ao escolher um artigo vai-se buscá-lo à ficha individual. Como chega depois, só
    // se escreve se o preço ainda for o que estava quando se escolheu — senão, quem
    // entretanto escreveu 42,50 via o seu número ser substituído pelo do catálogo.
    const priceBeforeFetch =
      lines.find((line) => line._key === key)?.unitPrice ?? 0;
    getArticleById(articleId)
      .then((article) => {
        if (article.referencePrice === null || article.referencePrice === undefined) {
          return;
        }
        setLines((prev) =>
          prev.map((line) =>
            line._key === key &&
            line.articleId === articleId &&
            line.unitPrice === priceBeforeFetch
              ? { ...line, unitPrice: article.referencePrice as number }
              : line,
          ),
        );
      })
      .catch(() => {
        setPriceWarning(
          "Não foi possível ir buscar o preço de referência do artigo. Confirme o preço unitário.",
        );
      });
  };

  const addLine = () => {
    const last = lines[lines.length - 1];
    setLines((prev) => [
      ...prev,
      {
        _key: nextKey(),
        ...blankLineFields(last?.areaName || DEFAULT_GROUP, last?.chapterName || DEFAULT_GROUP),
      },
    ]);
    setSavedMessage(null);
  };

  const removeLine = (key: number) => {
    setLines((prev) => prev.filter((line) => line._key !== key));
    setSavedMessage(null);
  };

  const total = useMemo(
    () => lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0),
    [lines],
  );

  const handleSave = async () => {
    if (isSaving) return;
    const cleaned: WorkTypeTemplateLineInput[] = lines
      .map((line) => ({
        areaName: line.areaName.trim() || DEFAULT_GROUP,
        chapterName: line.chapterName.trim() || DEFAULT_GROUP,
        articleId: line.articleId ?? null,
        code: line.code.trim(),
        description: line.description.trim(),
        unit: line.unit.trim(),
        quantity: line.quantity,
        quantityPerArea: line.quantityPerArea,
        unitPrice: line.unitPrice,
        marginPercent: line.marginPercent ?? null,
      }))
      .filter((line) => line.description !== "");

    setIsSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const detail = await saveWorkTypeTemplate(workTypeId, cleaned);
      setLines(detail.templateLines.map(toEditable));
      setSavedMessage(
        cleaned.length === 0
          ? "Template guardado (sem linhas). Linhas sem descrição são ignoradas."
          : `Template guardado com sucesso (${detail.templateLines.length} linha(s)).`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível guardar o template.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title={name ? `Template — ${name}` : "Template do tipo de obra"}
        subtitle="Capítulos e artigos típicos usados para gerar a estrutura inicial de um orçamento"
        backButton={{ onClick: () => navigate("/catalogo") }}
        primaryAction={{
          label: isSaving ? "A guardar…" : "Guardar template",
          onClick: handleSave,
          disabled: isSaving || isLoading,
        }}
      />

      <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-background">
        {error && (
          <div className="mb-4 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3" style={{ borderRadius: "var(--radius)" }}>{error}</div>
        )}
        {savedMessage && (
          <div className="mb-4 border border-primary/20 bg-primary/10 text-foreground px-4 py-3" style={{ borderRadius: "var(--radius)" }}>{savedMessage}</div>
        )}
        {priceWarning && (
          <div className="mb-4 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3" style={{ borderRadius: "var(--radius)" }}>{priceWarning}</div>
        )}

        {isLoading ? (
          <div className="text-secondary-text">A carregar…</div>
        ) : (
          <div className="space-y-4">
            <p className="text-secondary-text text-sm">
              Marque <strong>“por m²”</strong> nas linhas cuja quantidade deve ser multiplicada pela área da obra ao gerar o orçamento. As restantes mantêm a quantidade fixa. Linhas sem descrição são ignoradas ao guardar.
            </p>

            <div className="bg-card border border-border overflow-x-auto" style={{ borderRadius: "var(--radius-card)" }}>
              <table className="w-full" style={{ minWidth: "1040px" }}>
                <thead>
                  <tr className="border-b border-border bg-muted text-left text-sm">
                    <th className="px-2 py-3" style={{ width: "150px" }}>Área</th>
                    <th className="px-2 py-3" style={{ width: "150px" }}>Capítulo</th>
                    <th className="px-2 py-3" style={{ width: "180px" }}>Artigo (catálogo)</th>
                    <th className="px-2 py-3">Descrição</th>
                    <th className="px-2 py-3" style={{ width: "80px" }}>Unid.</th>
                    <th className="px-2 py-3" style={{ width: "90px" }}>Qtd.</th>
                    <th className="px-2 py-3 text-center" style={{ width: "64px" }}>/m²</th>
                    <th className="px-2 py-3" style={{ width: "110px" }}>Preço (€)</th>
                    <th className="px-2 py-3" style={{ width: "90px" }}>Margem %</th>
                    <th className="px-2 py-3" style={{ width: "64px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 ? (
                    <tr><td colSpan={10} className="px-2 py-6 text-center text-secondary-text">Sem linhas. Adicione a primeira linha do template.</td></tr>
                  ) : lines.map((line) => (
                    <tr key={line._key} className="border-b border-border last:border-b-0 align-top">
                      <td className="px-2 py-2"><input value={line.areaName} maxLength={200} placeholder="Ex: Interior" onChange={(e) => updateLine(line._key, { areaName: e.target.value })} className={inputClass} /></td>
                      <td className="px-2 py-2"><input value={line.chapterName} maxLength={200} placeholder="Ex: Demolições" onChange={(e) => updateLine(line._key, { chapterName: e.target.value })} className={inputClass} /></td>
                      <td className="px-2 py-2">
                        <SearchableSelect<number>
                          ariaLabel="Artigo (catálogo)"
                          panelPosition="fixed"
                          value={line.articleId}
                          onChange={(next, option) =>
                            handleArticleSelect(line._key, next, option)
                          }
                          onSearch={searchArticleOptions}
                          serverPageSize={LOOKUP_SEARCH_PAGE_SIZE}
                          options={
                            line.articleId != null
                              ? [
                                  {
                                    value: line.articleId,
                                    label: articleSeedLabel(line),
                                  },
                                ]
                              : undefined
                          }
                          placeholder="— livre —"
                          searchPlaceholder="Procurar artigo…"
                        />
                      </td>
                      <td className="px-2 py-2"><input value={line.description} maxLength={500} placeholder="Descrição da linha" onChange={(e) => updateLine(line._key, { description: e.target.value })} className={inputClass} /></td>
                      <td className="px-2 py-2"><input value={line.unit} maxLength={20} placeholder="m2" onChange={(e) => updateLine(line._key, { unit: e.target.value })} className={inputClass} /></td>
                      <td className="px-2 py-2"><input type="number" min="0" step="0.0001" value={line.quantity} onChange={(e) => updateLine(line._key, { quantity: parseNumber(e.target.value) })} className={`${inputClass} text-right`} /></td>
                      <td className="px-2 py-2 text-center"><input type="checkbox" checked={line.quantityPerArea} onChange={(e) => updateLine(line._key, { quantityPerArea: e.target.checked })} className="w-4 h-4" /></td>
                      <td className="px-2 py-2"><input type="number" min="0" step="0.01" value={line.unitPrice} onChange={(e) => updateLine(line._key, { unitPrice: parseNumber(e.target.value) })} className={`${inputClass} text-right`} /></td>
                      <td className="px-2 py-2"><input type="number" min="0" step="0.01" value={line.marginPercent ?? ""} placeholder="—" onChange={(e) => updateLine(line._key, { marginPercent: e.target.value.trim() === "" ? null : parseNumber(e.target.value) })} className={`${inputClass} text-right`} /></td>
                      <td className="px-2 py-2 text-center"><button type="button" className="text-destructive underline" onClick={() => removeLine(line._key)}>Remover</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <button type="button" onClick={addLine} className="inline-flex items-center justify-center px-4 py-2 bg-secondary border border-border text-foreground cursor-pointer" style={{ borderRadius: "var(--radius)" }}>
                + Adicionar linha
              </button>
              <div className="text-secondary-text text-sm">
                {lines.length} linha(s) · Total base (sem área/IVA): <span className="text-foreground font-medium">{formatCurrency(total)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
